import type { TicketSelection } from "./types";

/**
 * The seats one ticket type buys at checkout, in the order the tickets are
 * sent to the API. See plans/booking-seats.md (Phase 2).
 *
 * This is the ONE client derivation of "how many attendees does this order
 * seat": every checkout hook's `totalGuestCount` is the sum of these groups.
 * It mirrors the server's `computeReserveSeatCounts` and `BookingSeat.planSeats`
 * (quantity × guests per ticket, a missing guests-per-ticket is 1, 0 is a
 * seatless add-on), so the seats the shopper fills are the seats the booking
 * gets, in the same order.
 */
export type TicketSeatGroup = {
  ticketTypeId: string;
  label: string;
  seatCount: number;
  /** The ticket's optional age range: each seat's attendee is checked against it (soft). */
  minAge?: number | null;
  maxAge?: number | null;
};

type SeatTicketType = {
  id: string;
  name?: string | null;
  numberOfGuestsPerTicket?: number | null;
  minAge?: number | null;
  maxAge?: number | null;
};

/** Guests one ticket seats. Same rule as the server: missing → 1, 0 → none. */
export function guestsPerTicket(ticketType: SeatTicketType | undefined): number {
  const raw = ticketType?.numberOfGuestsPerTicket;
  if (raw === undefined || raw === null) return 1;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 1;
}

/**
 * One group per selected ticket type that seats anyone, in selection order. A
 * ticket type selected twice is one group; a group that seats nobody (an
 * add-on, a quantity of 0) is left out.
 */
export function buildTicketSeatGroups(
  selections: readonly TicketSelection[],
  ticketTypes: readonly SeatTicketType[],
): TicketSeatGroup[] {
  const groups: TicketSeatGroup[] = [];
  const byId = new Map<string, TicketSeatGroup>();
  for (const selection of selections) {
    const quantity = Math.max(0, Math.floor(Number(selection.quantity) || 0));
    const ticketType = ticketTypes.find((tt) => tt.id === selection.ticketTypeId);
    const seats = quantity * guestsPerTicket(ticketType);
    if (seats <= 0) continue;
    const existing = byId.get(selection.ticketTypeId);
    if (existing) {
      existing.seatCount += seats;
      continue;
    }
    const group: TicketSeatGroup = {
      ticketTypeId: selection.ticketTypeId,
      label: ticketType?.name?.trim() || "Ticket",
      seatCount: seats,
      ...(ticketType?.minAge != null ? { minAge: ticketType.minAge } : {}),
      ...(ticketType?.maxAge != null ? { maxAge: ticketType.maxAge } : {}),
    };
    byId.set(group.ticketTypeId, group);
    groups.push(group);
  }
  return groups;
}

/** Seats the whole order buys. */
export function totalSeatCount(groups: readonly TicketSeatGroup[]): number {
  return groups.reduce((sum, g) => sum + g.seatCount, 0);
}
